import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import tokens from "../../theme/tokens";

const shimmer = keyframes`
  0%   { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const SkeletonWrapper = styled.div`
  padding: 8px 0;
`;

const SkeletonHeader = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$columns};
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid ${tokens.border.default};
  background: ${tokens.surface.page};
  font-family: ${tokens.typography.fontFamily};
  font-size: 11px;
  font-weight: ${tokens.typography.weight.semibold};
  letter-spacing: 0.04em;
  color: ${tokens.text.secondary};
  text-transform: uppercase;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$columns};
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${tokens.border.light};
  min-height: 44px;
`;

const Bar = styled.div`
  height: ${(p) => p.$height || 12}px;
  width: ${(p) => p.$width || "80%"};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${tokens.border.light} 0%,
    ${tokens.surface.hover} 50%,
    ${tokens.border.light} 100%
  );
  background-size: 200px 100%;
  background-repeat: no-repeat;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const Pill = styled(Bar)`
  border-radius: 9999px;
  height: 20px;
`;

/**
 * Six-column loading placeholder tailored to `MatterDetailsTableColumn`:
 *   Name | Added On | Last Modified | Status | Category | (actions)
 *
 * This is intentionally NOT a generic Table primitive — the column grid,
 * header labels, and bar widths are hand-tuned to match the matter-details
 * document list so the layout stays stable across the loading → loaded
 * transition. Any other Table consumer that wants a skeleton should ship
 * its own component with its own column layout and pass it to `Table`
 * via the `loadingComponent` prop.
 */
const MatterDetailsSkeleton = ({ rows = 6 }) => {
  const columns = "minmax(0, 3fr) 140px 160px 140px 200px 40px";

  return (
    <SkeletonWrapper aria-busy="true" aria-live="polite">
      <SkeletonHeader $columns={columns}>
        <span>Name</span>
        <span>Added On</span>
        <span>Last Modified</span>
        <span>Status</span>
        <span>Category</span>
        <span />
      </SkeletonHeader>
      {Array.from({ length: rows }).map((_, idx) => (
        <SkeletonRow key={idx} $columns={columns}>
          <Bar $width={`${55 + ((idx * 7) % 30)}%`} $height={12} />
          <Bar $width="70px" $height={12} />
          <Bar $width="120px" $height={12} />
          <Pill $width={idx % 2 === 0 ? "84px" : "72px"} />
          <Pill $width={`${90 + ((idx * 13) % 60)}px`} />
          <Bar $width="20px" $height={14} />
        </SkeletonRow>
      ))}
    </SkeletonWrapper>
  );
};

MatterDetailsSkeleton.propTypes = {
  rows: PropTypes.number,
};

export default MatterDetailsSkeleton;
